import { normalizePathInput, validatePath } from '@dashboard/modules/segment/services/validationHelpers'
import type { Question } from '@dashboard/types/config'
import { validateAnswerCondition } from '@dashboard/modules/segment/services/answerConditions'

/**
 * Condition Validator for Righty AI-Generated Conditions
 *
 * This module validates conditions generated by Righty to ensure they conform
 * to the expected structure before being saved to the config.
 */

// Valid source types and their allowed $type values
// Note: 'partner' source is dynamic based on the connected ESP (e.g., 'convertkit', 'mailchimp')
const VALID_SOURCE_TYPES: Record<string, string[]> = {
  pageviews: ['landingpage', 'path', 'domain', 'newvisitor', 'returningvisitor'],
  referrer: ['direct', 'referral', 'domain', 'custom'],
  utm: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'],
  query: ['custom'],
  segments: ['*'], // Dynamic - $type is the dimension ID (dim_*)
  location: ['country', 'city'],
  device: ['os', 'browser'],
  date: ['date', 'time', 'weekday', 'month'],
  score: ['*'], // Dynamic - $type is the score ID
  jsdata: ['custom'],
  rightmessage: ['widget', 'flowStep', 'question'],
  wordpress: ['role', 'status', 'post_type']
}

// Partner sources are dynamic based on the ESP - they use the ESP name as $source
// These are the common $type values for partner sources
const PARTNER_TYPES = ['isAnonymous', 'isSubscriber', 'tags', 'customField', 'lists']

// Valid operator values for text-based matching (getOperatorOptions)
const TEXT_OPERATORS = [
  'equals',
  'does not equal',
  'contains',
  'does not contain',
  'starts with',
  'ends with',
  'is set',
  'is not set',
  'glob' // Wildcard matching - added for path patterns
]

// Valid operator values for inclusion-based matching (getInclusionOptions)
const INCLUSION_OPERATORS = ['include any', 'do not include any', 'is set', 'is not set']

// Valid operator values for date matching (getDateOperatorOptions)
const DATE_OPERATORS = ['between', 'equals', 'before', 'after']

// Valid operator values for mathematical comparisons (getMathematicalOperatorOptions)
const MATH_OPERATORS = ['equals', 'does not equal', 'gt', 'lt', 'gte', 'lte']

// Valid occurrence values for pageviews:path
const PATH_OCCURRENCES = ['last', 'penultimate', 'any', 'not_any']

// Valid occurrenceOperator values for pageviews:path when occurrence is 'any'
const PATH_OCCURRENCE_OPERATORS = ['equals', 'gte', 'lte', 'gt', 'lt']

// Valid occurrence values for UTM and query sources
const TOUCH_OCCURRENCES = ['last touch', 'last', 'first touch', 'first', 'any']

// Source+type combinations that require specific operators
const OPERATOR_REQUIREMENTS: Record<string, string[]> = {
  // Text operators
  'pageviews:landingpage': TEXT_OPERATORS,
  'pageviews:path': [...TEXT_OPERATORS, 'matches pattern'],
  'pageviews:domain': [...TEXT_OPERATORS, 'matches pattern'],
  'referrer:custom': TEXT_OPERATORS,
  'location:city': TEXT_OPERATORS,
  'utm:*': TEXT_OPERATORS,
  'query:custom': TEXT_OPERATORS,
  'jsdata:custom': TEXT_OPERATORS,

  // Inclusion operators
  'location:country': INCLUSION_OPERATORS,
  'device:os': INCLUSION_OPERATORS,
  'device:browser': INCLUSION_OPERATORS,
  'segments:*': INCLUSION_OPERATORS,
  'date:weekday': INCLUSION_OPERATORS,
  'date:month': INCLUSION_OPERATORS,
  'partner:tags': INCLUSION_OPERATORS,
  'partner:lists': INCLUSION_OPERATORS,

  // Date operators
  'date:date': DATE_OPERATORS,
  'date:time': DATE_OPERATORS,

  // Mathematical operators
  'score:*': MATH_OPERATORS
}

// Required fields per source+type (beyond $source and $type)
const REQUIRED_FIELDS: Record<string, string[]> = {
  'pageviews:landingpage': ['operator', 'value'],
  'pageviews:path': ['operator', 'value', 'occurrence'],
  'pageviews:domain': ['operator', 'value'],
  // newvisitor and returningvisitor don't require additional fields
  'pageviews:newvisitor': [],
  'pageviews:returningvisitor': [],

  'referrer:direct': [],
  'referrer:referral': [],
  'referrer:domain': ['domain'],
  'referrer:custom': ['operator', 'custom_referrer'],

  'utm:utm_source': ['occurrence', 'operator'],
  'utm:utm_medium': ['occurrence', 'operator'],
  'utm:utm_campaign': ['occurrence', 'operator'],
  'utm:utm_term': ['occurrence', 'operator'],
  'utm:utm_content': ['occurrence', 'operator'],

  'query:custom': ['query', 'occurrence', 'operator'],

  'location:country': ['operator', 'value'],
  'location:city': ['operator', 'value'],

  'device:os': ['operator'],
  'device:browser': ['operator'],

  'date:date': ['operator', 'value'],
  'date:time': ['operator', 'value'],
  'date:weekday': ['operator', 'value'],
  'date:month': ['operator', 'value'],

  'segments:*': ['operator', 'segmentId'],

  'score:*': ['operator', 'value'],

  'jsdata:custom': ['key', 'operator']
}

export interface ValidationError {
  field: string
  message: string
  suggestion?: string
}

export interface PageviewPathValidationOptions {
  strictPageviewPaths?: boolean
  questions?: readonly Question[]
}

/**
 * Validates a single condition object.
 *
 * Existing configuration retains runtime-supported pageview URL values by default.
 * AI-authored conditions must opt into strict pathname-only validation.
 */
export function validateCondition(
  condition: Record<string, unknown>,
  { strictPageviewPaths = false, questions }: PageviewPathValidationOptions = {}
): ValidationError[] {
  const errors: ValidationError[] = []

  // Check for required $source
  if (!condition.$source) {
    errors.push({
      field: '$source',
      message: 'Missing required $source field',
      suggestion: 'Add a valid $source like "pageviews", "location", "referrer", etc.'
    })
    return errors // Can't validate further without $source
  }

  const source = String(condition.$source)
  if (source === 'answers') {
    return validateAnswerCondition(condition, questions)
  }

  // Check if source is valid (or is a partner source)
  const isValidSource = source in VALID_SOURCE_TYPES
  const isPartnerSource = !isValidSource && PARTNER_TYPES.includes(String(condition.$type || ''))

  // Common invalid sources that Righty might generate
  const INVALID_SOURCE_SUGGESTIONS: Record<string, string> = {
    sessions: 'Use "location" for geographic targeting or "referrer" for traffic source',
    traffic: 'Use "referrer" for traffic source conditions',
    geo: 'Use "location" for geographic targeting',
    browser: 'Use "device" with $type: "browser"',
    os: 'Use "device" with $type: "os"',
    time: 'Use "date" with $type: "date", "time", "weekday", or "month"'
  }

  if (!isValidSource && !isPartnerSource) {
    const suggestion = INVALID_SOURCE_SUGGESTIONS[source.toLowerCase()]
    errors.push({
      field: '$source',
      message: `Invalid source "${source}"`,
      suggestion: suggestion || `Valid sources are: ${Object.keys(VALID_SOURCE_TYPES).join(', ')}`
    })
    return errors // Can't validate further without valid source
  }

  // Check for required $type (unless source doesn't need one)
  const sourceTypes = VALID_SOURCE_TYPES[source]
  if (sourceTypes && sourceTypes.length > 0 && sourceTypes[0] !== '*') {
    if (!condition.$type) {
      errors.push({
        field: '$type',
        message: `Missing required $type field for source "${source}"`,
        suggestion: `Valid types for "${source}" are: ${sourceTypes.join(', ')}`
      })
      return errors
    }

    const conditionType = String(condition.$type)
    if (!sourceTypes.includes(conditionType) && !sourceTypes.includes('*')) {
      errors.push({
        field: '$type',
        message: `Invalid type "${conditionType}" for source "${source}"`,
        suggestion: `Valid types are: ${sourceTypes.join(', ')}`
      })
      return errors
    }
  }

  const conditionType = String(condition.$type || '')

  // Validate operator if this source+type requires one
  const operatorKey = `${source}:${conditionType}`
  const wildcardKey = `${source}:*`
  const allowedOperators = OPERATOR_REQUIREMENTS[operatorKey] || OPERATOR_REQUIREMENTS[wildcardKey]

  if (allowedOperators && condition.operator) {
    const operator = String(condition.operator)
    if (!allowedOperators.includes(operator)) {
      errors.push({
        field: 'operator',
        message: `Invalid operator "${operator}" for ${source}:${conditionType}`,
        suggestion: `Valid operators are: ${allowedOperators.join(', ')}`
      })
    }
  }

  // Validate required fields
  const requiredKey = `${source}:${conditionType}`
  const requiredWildcard = `${source}:*`
  const requiredFields = REQUIRED_FIELDS[requiredKey] || REQUIRED_FIELDS[requiredWildcard] || []

  for (const field of requiredFields) {
    // Skip value check if operator is 'is set' or 'is not set'
    if (field === 'value' && (condition.operator === 'is set' || condition.operator === 'is not set')) {
      continue
    }

    if (condition[field] === undefined || condition[field] === null || condition[field] === '') {
      errors.push({
        field,
        message: `Missing required field "${field}" for ${source}:${conditionType}`
      })
    }
  }

  if (
    source === 'pageviews' &&
    conditionType === 'path' &&
    condition.operator === 'matches pattern' &&
    condition.occurrence !== 'last'
  ) {
    errors.push({
      field: 'occurrence',
      message: '"matches pattern" on pageviews:path requires occurrence "last" (the current page).'
    })
  }

  // Validate occurrence for pageviews:path
  if (source === 'pageviews' && conditionType === 'path' && condition.occurrence) {
    const occurrence = String(condition.occurrence)
    if (!PATH_OCCURRENCES.includes(occurrence)) {
      errors.push({
        field: 'occurrence',
        message: `Invalid occurrence "${occurrence}" for pageviews:path`,
        suggestion: `Valid occurrences are: ${PATH_OCCURRENCES.join(', ')}`
      })
    }

    // When occurrence is 'any' (Has visited), occurrenceOperator and occurrenceCount are required
    if (occurrence === 'any') {
      if (!condition.occurrenceOperator) {
        errors.push({
          field: 'occurrenceOperator',
          message: 'Missing required occurrenceOperator when occurrence is "any"',
          suggestion: `Valid values are: ${PATH_OCCURRENCE_OPERATORS.join(', ')} (default: "gte")`
        })
      } else if (!PATH_OCCURRENCE_OPERATORS.includes(String(condition.occurrenceOperator))) {
        errors.push({
          field: 'occurrenceOperator',
          message: `Invalid occurrenceOperator "${String(condition.occurrenceOperator)}"`,
          suggestion: `Valid values are: ${PATH_OCCURRENCE_OPERATORS.join(', ')}`
        })
      }

      if (condition.occurrenceCount === undefined || condition.occurrenceCount === null) {
        errors.push({
          field: 'occurrenceCount',
          message: 'Missing required occurrenceCount when occurrence is "any"',
          suggestion: 'Set to a number (e.g., 1 for "at least 1 time")'
        })
      }
    }
  }

  // Validate occurrence for UTM and query sources
  if ((source === 'utm' || source === 'query') && condition.occurrence) {
    const occurrence = String(condition.occurrence)
    if (!TOUCH_OCCURRENCES.includes(occurrence)) {
      errors.push({
        field: 'occurrence',
        message: `Invalid occurrence "${occurrence}" for ${source}`,
        suggestion: `Valid occurrences are: ${TOUCH_OCCURRENCES.join(', ')}`
      })
    }
  }
  if (
    source === 'pageviews' &&
    (conditionType === 'path' || conditionType === 'landingpage') &&
    typeof condition.value === 'string' &&
    condition.operator !== 'matches pattern'
  ) {
    const error = validatePath(condition.value, { strict: strictPageviewPaths })
    if (error) {
      errors.push({
        field: 'value',
        message: error
      })
    }
  }

  return errors
}

/**
 * Validates a logic definition object (or/and structure).
 *
 * Existing configuration retains runtime-supported pageview URL values by default.
 * AI-authored conditions must opt into strict pathname-only validation.
 */
export function validateLogicDefinition(
  definition: Record<string, unknown>,
  options: PageviewPathValidationOptions = {}
): ValidationError[] {
  const errors: ValidationError[] = []

  function validateNode(node: unknown, path: string): void {
    if (!node || typeof node !== 'object') {
      errors.push({
        field: path,
        message: 'Invalid node - expected object'
      })
      return
    }

    const nodeObj = node as Record<string, unknown>

    // Check for OR groups
    if (Array.isArray(nodeObj.or)) {
      nodeObj.or.forEach((child, i) => {
        validateNode(child, `${path}.or[${i}]`)
      })
      return
    }

    // Check for AND groups
    if (Array.isArray(nodeObj.and)) {
      nodeObj.and.forEach((child, i) => {
        validateNode(child, `${path}.and[${i}]`)
      })
      return
    }

    if (nodeObj.not) {
      validateNode(nodeObj.not, `${path}.not`)
      return
    }

    // This is a leaf condition - validate it
    if (nodeObj.$source) {
      const conditionErrors = validateCondition(nodeObj, options)
      conditionErrors.forEach((err) => {
        errors.push({
          ...err,
          field: `${path}.${err.field}`
        })
      })
    }
  }

  validateNode(definition, 'root')
  return errors
}

/**
 * Auto-fixes common issues in conditions.
 *
 * Existing configuration retains runtime-supported pageview URL values by default.
 * AI-authored conditions must opt into strict pathname-only normalization.
 */
export function autoFixCondition(
  condition: Record<string, unknown>,
  { strictPageviewPaths = false }: PageviewPathValidationOptions = {}
): Record<string, unknown> {
  const fixed = { ...condition }

  // Fix common source mistakes
  const source = String(fixed.$source || '').toLowerCase()
  if (source === 'sessions') {
    // 'sessions' is not valid - likely meant 'pageviews' for session tracking
    // or 'location' for geographic data
    console.warn('[Condition Validator] Auto-fixing invalid source "sessions"')
    // Can't auto-fix this one reliably - depends on context
  }

  // Ensure operator is lowercase and properly formatted
  if (fixed.operator && typeof fixed.operator === 'string') {
    fixed.operator = fixed.operator.toLowerCase()
  }

  if (
    fixed.$source === 'pageviews' &&
    (fixed.$type === 'path' || fixed.$type === 'landingpage') &&
    typeof fixed.value === 'string' &&
    fixed.operator !== 'matches pattern'
  ) {
    fixed.value = normalizePathInput(fixed.value, { strict: strictPageviewPaths }).value
  }

  // Add default occurrence for pageviews:path if missing
  if (fixed.$source === 'pageviews' && fixed.$type === 'path' && !fixed.occurrence) {
    fixed.occurrence = fixed.operator === 'matches pattern' ? 'last' : 'any'
  }

  // Add default timeframe for pageviews:path when occurrence is 'any' or 'not_any'
  if (
    fixed.$source === 'pageviews' &&
    fixed.$type === 'path' &&
    (fixed.occurrence === 'any' || fixed.occurrence === 'not_any') &&
    !fixed.timeframe
  ) {
    fixed.timeframe = 'ever'
  }

  // Add default unit for pageviews:path when timeframe is 'within' and duration is set but unit is missing
  if (
    fixed.$source === 'pageviews' &&
    fixed.$type === 'path' &&
    fixed.timeframe === 'within' &&
    fixed.duration &&
    !fixed.unit
  ) {
    fixed.unit = 'day'
  }

  // Normalize plural unit values to singular (AI often outputs "days" instead of "day")
  if (fixed.unit && typeof fixed.unit === 'string') {
    const unitMap: Record<string, string> = { days: 'day', hours: 'hour', weeks: 'week', months: 'month' }
    fixed.unit = unitMap[fixed.unit] || fixed.unit
  }

  // Add default occurrence for UTM if missing
  if (fixed.$source === 'utm' && !fixed.occurrence) {
    fixed.occurrence = 'any'
  }

  return fixed
}
